import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Skeleton,
  Alert,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  Edit as EditIcon,
  TaskAlt as CompleteIcon,
  RadioButtonUnchecked as EmptyIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import api from "../../api";
import { PURPLE, StepBadge } from "./ResourceAllocationShared";
import EditGroupResourceDialog from "./EditGroupResourceDialog";

const stageChip = {
  awaiting_tutor: { label: "Awaiting lead tutor", color: "#ed6c02", bg: "#ed6c0218" },
  rejected_by_tutor: { label: "Rejected by lead tutor", color: "#d32f2f", bg: "#d32f2f18" },
  awaiting_admin: { label: "Awaiting admin", color: "#0288d1", bg: "#0288d118" },
  approved: { label: "Visible to students", color: "#2e7d32", bg: "#2e7d3218" },
  completed: { label: "Complete", color: "#2e7d32", bg: "#2e7d3218" },
  rejected: { label: "Not approved", color: "#d32f2f", bg: "#d32f2f18" },
};

const chipFor = (r) => stageChip[r.stage] || stageChip.awaiting_tutor;

/**
 * The selected group's tutor-uploaded content, actioned in place.
 *
 * The Staff tab decides who may upload against this group; this tab is where what they uploaded
 * gets reviewed — so an admin can staff a group and clear its queue without leaving the page. It
 * is the same data and the same endpoints as Admin → Tutor Content Review, scoped to one plan.
 *
 * Approving here is what makes a file visible to the group's students; "Complete" afterwards is
 * bookkeeping only and changes no visibility.
 */
export default function GroupContentTab({
  selectedPlan,
  planId,
  acdemicCourseId,
  initialWeekFilter = "",
  initialSubjectFilter = "",
  focusToken = 0,
}) {
  const [rows, setRows] = useState([]);
  const [weeks, setWeeks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [weekFilter, setWeekFilter] = useState(initialWeekFilter);
  const [subjectFilter, setSubjectFilter] = useState(initialSubjectFilter);
  const [stageFilter, setStageFilter] = useState("all");

  const [target, setTarget] = useState(null);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    if (!planId) {
      setRows([]);
      return;
    }
    setLoading(true);
    setError("");
    try {
      const params = { course_plan_id: planId, status: "all" };
      if (weekFilter) params.plan_week_id = weekFilter;
      if (subjectFilter) params.subject_id = subjectFilter;
      const res = await api.get("admin/tutor-resources", { params });
      setRows(Array.isArray(res.data?.data) ? res.data.data : []);
    } catch (e) {
      setError(e?.response?.data?.message || "Could not load this group's content.");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [planId, weekFilter, subjectFilter]);

  useEffect(() => {
    load();
  }, [load]);

  // Reset the filters when the group changes — a week id from another plan matches nothing. The
  // same effect adopts the filters the Content Allocation tab asks for when an admin clicks a group
  // content chip there; focusToken changes on every such click, so clicking the same chip twice
  // still re-applies it.
  useEffect(() => {
    setWeekFilter(initialWeekFilter);
    setSubjectFilter(initialSubjectFilter);
    setStageFilter("all");
  }, [planId, focusToken, initialWeekFilter, initialSubjectFilter]);

  useEffect(() => {
    if (!planId) {
      setWeeks([]);
      return undefined;
    }
    let cancelled = false;
    api
      .get(`admin/course-plans/${planId}/weeks`)
      .then((res) => {
        if (!cancelled) {
          // The endpoint answers { weeks: [...] }; data was always undefined, so the filter never showed.
          const list = res.data?.weeks ?? res.data?.data;
          setWeeks(Array.isArray(list) ? list : []);
        }
      })
      .catch(() => {
        // Without weeks the filter simply does not render; the list still works.
        if (!cancelled) setWeeks([]);
      });
    return () => {
      cancelled = true;
    };
  }, [planId]);

  // Derived from the rows themselves so the filter can never offer a subject with nothing in it.
  const subjects = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      if (r.subject?.id) map.set(r.subject.id, r.subject.name);
    });
    return [...map.entries()].map(([id, name]) => ({ id, name }));
  }, [rows]);

  const visible = useMemo(
    () => (stageFilter === "all" ? rows : rows.filter((r) => r.stage === stageFilter)),
    [rows, stageFilter]
  );

  const submit = async () => {
    if (!target) return;
    setSaving(true);
    try {
      await api.post(`admin/tutor-resources/${target.row.id}/review`, {
        status: target.status,
        notes: notes || null,
      });
      toast.success(
        target.status === "approved"
          ? "Approved — now visible to this group."
          : "Rejected — it stays hidden from students."
      );
      setTarget(null);
      setNotes("");
      load();
    } catch (e) {
      toast.error(
        e?.response?.data?.data?.error || e?.response?.data?.message || "Could not save the review."
      );
    } finally {
      setSaving(false);
    }
  };

  const complete = async (row) => {
    setCompleting(row.id);
    try {
      await api.post(`admin/tutor-resources/${row.id}/complete`);
      toast.success("Marked complete.");
      load();
    } catch (e) {
      toast.error(
        e?.response?.data?.data?.error || e?.response?.data?.message || "Could not mark it complete."
      );
    } finally {
      setCompleting(null);
    }
  };

  if (!selectedPlan) {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <EmptyIcon sx={{ fontSize: 36, color: "#e2e8f0", mb: 1 }} />
        <Typography variant="body2" sx={{ color: "#374151", fontWeight: 700 }}>
          Select a group from the sidebar to review its content
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1.25} alignItems="center" mb={0.5}>
        <StepBadge n={4} />
        <Typography variant="subtitle1" fontWeight={700}>
          Content uploaded for {selectedPlan.name}
        </Typography>
      </Stack>
      <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700 }} display="block" mb={1.5}>
        Uploaded by the staff on the Staff tab. A tutor assistant's file needs the lead tutor's
        approval before it reaches you; approving it here is what makes it visible to students.
      </Typography>

      <Stack direction="row" spacing={1} mb={2} flexWrap="wrap" useFlexGap alignItems="center">
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Stage</InputLabel>
          <Select value={stageFilter} label="Stage" onChange={(e) => setStageFilter(e.target.value)}>
            <MenuItem value="all">All stages</MenuItem>
            <MenuItem value="awaiting_tutor">Awaiting lead tutor</MenuItem>
            <MenuItem value="awaiting_admin">Awaiting admin</MenuItem>
            <MenuItem value="approved">Visible to students</MenuItem>
            <MenuItem value="completed">Complete</MenuItem>
            <MenuItem value="rejected">Not approved</MenuItem>
          </Select>
        </FormControl>

        {weeks.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Week</InputLabel>
            <Select value={weekFilter} label="Week" onChange={(e) => setWeekFilter(e.target.value)}>
              <MenuItem value="">All weeks</MenuItem>
              {weeks.map((w) => (
                <MenuItem key={w.id} value={w.id}>
                  {w.label || `Week ${w.week_number}`}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        {subjects.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Subject</InputLabel>
            <Select
              value={subjectFilter}
              label="Subject"
              onChange={(e) => setSubjectFilter(e.target.value)}
            >
              <MenuItem value="">All subjects</MenuItem>
              {subjects.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

        <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading} sx={{ color: PURPLE }}>
          Refresh
        </Button>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Stack spacing={2}>
          {[1, 2].map((n) => (
            <Skeleton key={n} variant="rounded" height={110} />
          ))}
        </Stack>
      ) : visible.length === 0 ? (
        <Alert severity="info">
          {rows.length === 0
            ? "No tutor has uploaded content for this group yet."
            : "No content matches these filters."}
        </Alert>
      ) : (
        <Stack spacing={2}>
          {visible.map((r) => {
            const chip = chipFor(r);
            const leadCleared = r.lead_status === undefined || r.lead_status === "approved";
            return (
              <Card
                key={r.id}
                variant="outlined"
                sx={{ borderRadius: 2, borderLeft: `4px solid ${chip.color}` }}
              >
                <CardContent>
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    alignItems={{ md: "center" }}
                    spacing={2}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Stack direction="row" spacing={1} sx={{ mb: 1, flexWrap: "wrap", gap: 0.5 }}>
                        <Chip
                          size="small"
                          label={chip.label}
                          sx={{ bgcolor: chip.bg, color: chip.color, fontWeight: 600 }}
                        />
                        {r.week?.week_number != null && (
                          <Chip size="small" variant="outlined" label={r.week.label || `Week ${r.week.week_number}`} />
                        )}
                        {r.subject?.name && (
                          <Chip size="small" variant="outlined" label={r.subject.name} />
                        )}
                        {r.topic?.name && (
                          <Chip
                            size="small"
                            variant="outlined"
                            label={
                              r.subtopic?.name ? `${r.topic.name} › ${r.subtopic.name}` : r.topic.name
                            }
                          />
                        )}
                      </Stack>
                      <Typography variant="h6" fontWeight={700}>
                        {r.title}
                      </Typography>
                      {r.description && (
                        <Typography variant="body2" color="text.secondary">
                          {r.description}
                        </Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" display="block">
                        Uploaded by {r.uploaded_by || "a tutor"}
                        {r.lead_reviewed_by ? ` · cleared by ${r.lead_reviewed_by}` : ""}
                      </Typography>
                      {r.lead_notes && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          Lead tutor's note: {r.lead_notes}
                        </Typography>
                      )}
                    </Box>

                    <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                      {r.file_url && (
                        <Button
                          size="small"
                          startIcon={<DownloadIcon />}
                          component="a"
                          href={r.file_url}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open
                        </Button>
                      )}
                      <Button
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => setEditing(r)}
                      >
                        Edit
                      </Button>
                      {r.approval_status === "approved" && !r.completed_at && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<CompleteIcon />}
                          disabled={completing === r.id}
                          onClick={() => complete(r)}
                        >
                          {completing === r.id ? "Saving…" : "Complete"}
                        </Button>
                      )}
                      {r.approval_status !== "approved" &&
                        (leadCleared ? (
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<ApproveIcon />}
                            onClick={() => {
                              setNotes("");
                              setTarget({ row: r, status: "approved" });
                            }}
                          >
                            Approve
                          </Button>
                        ) : (
                          <Tooltip title="This group's lead tutor has not approved it yet">
                            <span>
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<ApproveIcon />}
                                disabled
                              >
                                Approve
                              </Button>
                            </span>
                          </Tooltip>
                        ))}
                      {r.approval_status !== "rejected" && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<RejectIcon />}
                          onClick={() => {
                            setNotes("");
                            setTarget({ row: r, status: "rejected" });
                          }}
                        >
                          Reject
                        </Button>
                      )}
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>
            );
          })}
        </Stack>
      )}

      <EditGroupResourceDialog
        open={Boolean(editing)}
        resource={editing}
        planId={planId}
        acdemicCourseId={acdemicCourseId}
        weeks={weeks}
        onClose={() => setEditing(null)}
        onSaved={load}
      />

      <Dialog open={Boolean(target)} onClose={() => !saving && setTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {target?.status === "approved" ? "Approve this content?" : "Reject this content?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {target?.status === "approved"
              ? `"${target?.row?.title}" becomes visible to every student in ${selectedPlan.name}.`
              : `"${target?.row?.title}" stays hidden from students. The uploading tutor is told.`}
          </DialogContentText>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes for the tutor (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTarget(null)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color={target?.status === "approved" ? "success" : "error"}
            onClick={submit}
            disabled={saving}
          >
            {saving ? "Saving…" : target?.status === "approved" ? "Approve" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
